import {
  ComponentRef,
  Directive,
  effect,
  inject,
  input,
  InputSignal,
  isDevMode,
  OnDestroy,
  output,
  OutputEmitterRef,
  PendingTasks,
  Type,
  ViewContainerRef
} from '@angular/core';

/**
 * Renders a lazily-loaded component into its host `<ng-container>`. The component class is produced by a
 * loader (`() => import('...').then(m => m.X)`), so esbuild splits it into its own chunk that is fetched
 * only on pages that actually render the component. Used by the page-renderer and the Designer previews to
 * resolve `PageComponentDefinition.loadComponent` without statically importing every component.
 *
 * SSR: the dynamic import is registered as a {@link PendingTasks} task, so the server render awaits it and
 * the resolved component's markup is part of the SSR HTML (rather than an empty container).
 */
@Directive({
  selector: '[gsLazyComponent]',
  standalone: true
})
export class LazyComponentHostDirective implements OnDestroy {
  // Services
  private readonly viewContainerRef: ViewContainerRef = inject(ViewContainerRef);
  private readonly pendingTasks: PendingTasks = inject(PendingTasks);

  // Inputs
  /** Loader for the component class — MUST target a concrete module file so it can be code-split. */
  readonly loader: InputSignal<() => Promise<Type<unknown>>> = input.required<() => Promise<Type<unknown>>>({
    alias: 'gsLazyComponent'
  });
  /** Inputs applied to the rendered component, identical in shape to `ngComponentOutlet`'s `inputs`. */
  readonly componentInputs: InputSignal<Record<string, unknown>> = input<Record<string, unknown>>(
    {},
    { alias: 'gsLazyInputs' }
  );

  // Outputs
  /** Emits once the lazily-loaded component has been created and rendered into the host container. */
  readonly rendered: OutputEmitterRef<void> = output<void>();

  // Properties & Signals
  /** Reference to the currently rendered component, or undefined before the loader resolves. */
  private componentRef?: ComponentRef<unknown>;
  /** The loader that produced the current render, to detect when a re-load is required. */
  private resolvedLoader?: () => Promise<Type<unknown>>;

  constructor() {
    effect((onCleanup) => {
      const loader = this.loader();
      let active = true;
      onCleanup(() => (active = false));
      this.resolvedLoader = loader;
      const taskDone = this.pendingTasks.add();
      loader()
        .then((componentType) => {
          if (active && loader === this.resolvedLoader) this.render(componentType);
        })
        .catch((error) => {
          if (isDevMode()) console.error('[LazyComponentHost] failed to load component', error);
        })
        .finally(() => taskDone());
    });

    effect(() => {
      const inputs = this.componentInputs();
      if (this.componentRef) this.applyInputs(inputs);
    });
  }

  ngOnDestroy(): void {
    this.componentRef?.destroy();
  }

  // Methods
  /**
   * Creates the resolved component in the host container and wires its inputs.
   * @param componentType The resolved component class.
   */
  private render(componentType: Type<unknown>): void {
    this.viewContainerRef.clear();
    this.componentRef = this.viewContainerRef.createComponent(componentType);
    this.applyInputs(this.componentInputs());
    this.componentRef.changeDetectorRef.detectChanges();
    this.rendered.emit();
  }

  /**
   * Applies the input map to the rendered component via `setInput` (mirrors `ngComponentOutlet`).
   * @param inputs The input key/value map.
   */
  private applyInputs(inputs: Record<string, unknown>): void {
    if (!this.componentRef) return;
    for (const [key, value] of Object.entries(inputs)) {
      this.componentRef.setInput(key, value);
    }
  }
}
